import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

/**
 * Admin — pending wire/ACH deposits queue.
 *
 * Two flows live here:
 *   1. Preapproval deposits (awaiting_deposit) — wire arrives → admin confirms
 *      → contract PDF is generated + email sent + preapproval flips to active.
 *   2. Offer escrow deposits (pending, payment_method='wire_ach') — wire arrives
 *      → admin confirms → escrow flips to 'held'.
 *
 * Stripe is NEVER used for these deposits — money-motion is wire/ACH only.
 */
export default function WiresTab() {
  const [rows, setRows] = useState({ preapprovals: [], escrows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [reference, setReference] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/wires/pending")
      .then((r) => setRows(r.data))
      .catch(() => setRows({ preapprovals: [], escrows: [], total: 0 }))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openConfirm = (row) => {
    setConfirming(row);
    setReference(row.reference_code || "");
    setAmount(String(row.amount || ""));
  };

  const confirm = async () => {
    if (!reference.trim()) { toast.error("Reference (wire memo / ACH id) required"); return; }
    setBusy(true);
    try {
      const endpoint = confirming.kind === "preapproval"
        ? `/admin/preapprovals/${confirming.id}/mark-deposit-received`
        : `/admin/escrow/${confirming.id}/mark-wire-received`;
      await api.post(endpoint, {
        amount:    Number(amount) || confirming.amount,
        reference: reference.trim(),
        method:    "wire",
      });
      toast.success(`${confirming.kind === "preapproval" ? "Pre-approval" : "Escrow"} deposit confirmed`);
      setConfirming(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="admin-wires-tab">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-overline text-accent">Pending Wire / ACH Receipts</div>
          <div className="text-xs text-muted-foreground mt-1">{rows.total} pending · confirm each when funds land in FinanceLocal's transaction account.</div>
        </div>
        <Button variant="outline" className="rounded-none uppercase text-xs h-9" onClick={load} data-testid="wires-refresh">Refresh</Button>
      </div>

      <Section title={`Pre-approval Deposits (${rows.preapprovals.length})`} testid="wires-preapprovals">
        <Table items={rows.preapprovals} label="Preapproval" onConfirm={openConfirm} loading={loading} />
      </Section>

      <Section title={`Offer Escrow Deposits (${rows.escrows.length})`} testid="wires-escrows">
        <Table items={rows.escrows} label="Escrow" onConfirm={openConfirm} loading={loading} />
      </Section>

      {confirming && (
        <Dialog open onOpenChange={() => setConfirming(null)}>
          <DialogContent className="rounded-none max-w-md" data-testid="wires-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">
                Confirm {confirming.kind === "preapproval" ? "Pre-approval" : "Escrow"} Wire
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="border border-border p-3 text-xs">
                <div><b>User:</b> {confirming.user_email || confirming.user_id}</div>
                <div><b>Expected amount:</b> {fmtMoney(confirming.amount)}</div>
                <div><b>Expected memo:</b> <span className="font-mono">{confirming.reference_code || "—"}</span></div>
              </div>
              <div>
                <Label className="font-overline text-[10px]">Amount received ($)</Label>
                <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                        className="rounded-none" data-testid="wires-amount" />
              </div>
              <div>
                <Label className="font-overline text-[10px]">Reference / Wire memo *</Label>
                <Input value={reference} onChange={(e) => setReference(e.target.value)}
                        placeholder="FL-DEP-XXXXXXXX or the wire memo you saw on the statement"
                        className="rounded-none font-mono" data-testid="wires-reference" />
                <div className="text-[10px] text-muted-foreground mt-1">Auto-populated from the buyer's issued reference — override only if the actual memo differs.</div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setConfirming(null)}>Cancel</Button>
              <Button className="rounded-none uppercase" onClick={confirm} disabled={busy}
                      data-testid="wires-confirm-btn">Mark Received</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const Section = ({ title, testid, children }) => (
  <section>
    <div className="font-overline text-accent mb-2">{title}</div>
    <div className="border border-border overflow-x-auto" data-testid={testid}>{children}</div>
  </section>
);

function Table({ items, label, onConfirm, loading }) {
  if (loading) return <div className="p-6 text-center text-muted-foreground text-sm">Loading…</div>;
  if (!items || items.length === 0) return <div className="p-6 text-center text-muted-foreground text-sm">No pending {label.toLowerCase()} wires.</div>;
  return (
    <table className="w-full text-sm min-w-[720px]">
      <thead className="bg-muted/50 text-left">
        <tr>
          <Th>Created</Th><Th>User</Th><Th>Reference</Th><Th>Amount</Th><Th>Actions</Th>
        </tr>
      </thead>
      <tbody>
        {items.map((r) => (
          <tr key={r.id} className="border-t border-border" data-testid={`wires-row-${r.id}`}>
            <Td className="font-mono text-[10px]">{r.created_at?.slice(0, 16).replace("T", " ")}</Td>
            <Td className="text-[11px]">
              <div>{r.user_name || "—"}</div>
              <div className="text-muted-foreground font-mono text-[10px]">{r.user_email}</div>
            </Td>
            <Td className="font-mono text-[11px]">{r.reference_code || "—"}</Td>
            <Td className="font-mono-data text-[12px]">{fmtMoney(r.amount)}</Td>
            <Td>
              <Button size="sm" className="rounded-none text-[10px] h-7 uppercase"
                      data-testid={`wires-mark-${r.id}`} onClick={() => onConfirm(r)}>
                Mark Received
              </Button>
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const Th = ({ children }) => <th className="px-3 py-2 font-overline text-[10px] text-muted-foreground whitespace-nowrap">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2 ${className}`}>{children}</td>;
